import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, csrfCookieName, getCsrfToken } from '../config/apiBase';

// Re-exported so existing importers of these names keep working unchanged.
export { csrfCookieName, getCsrfToken };

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      // Attach X-CSRFToken on every unsafe request. Under the same-origin proxy
      // axios would attach it anyway, but this stays explicit so a local dev
      // build pointed at a remote backend (cross-site) still works.
      withXSRFToken: true,
      // Snapshot is a fallback only. The interceptor below re-reads the name
      // per request so a sandbox host talking to production Django picks up
      // `__Secure-csrftoken` after GET /api/auth/csrf/, not the staging name
      // that hostname used to imply.
      xsrfCookieName: csrfCookieName(),
      xsrfHeaderName: 'X-CSRFToken',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for FormData Content-Type handling
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        config.xsrfCookieName = csrfCookieName();
        // Let browser set Content-Type with boundary for FormData
        if (config.data instanceof FormData && config.headers) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Don't auto-redirect for auth endpoints - let them handle their own errors
        const isAuthEndpoint = error.config?.url?.includes('/api/auth/') ||
                               error.config?.url?.includes('/api/auth-token/');

        if (error.response?.status === 401 && !isAuthEndpoint) {
          // Session expired or invalid - clear cached UI state and redirect
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Generic request methods
  async get<T>(url: string, params?: Record<string, any>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.delete<T>(url, { data });
    return response.data;
  }

  // Clear cached UI state (the credential is now an HttpOnly session cookie)
  clearToken() {
    localStorage.removeItem('user');
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
